/**
 * IndexedDB local database service using Dexie.js.
 *
 * Provides offline-first storage for journal entries, pending media uploads,
 * the sync queue, and all Kira Productivity Suite module data.
 * Enforces storage limits (500 entries, 1GB).
 *
 * @module localDb
 */

import Dexie, { type Table } from 'dexie';
import type {
  LocalJournalEntry,
  PendingMedia,
  SyncQueueItem,
  Task,
  TaskList,
  Tag,
  Habit,
  HabitCompletion,
  CalendarEvent,
  PomodoroSession,
  CountdownTimer,
  ExternalCalendarConnection,
  Note,
} from '../types';

/** A key-value setting stored in appSettings. */
export interface AppSetting {
  /** Unique setting key (e.g. 'lastActiveModule'). */
  key: string;
  /** Setting value — can be any serializable type. */
  value: unknown;
}

/** Maximum number of entries allowed in local storage. */
const MAX_ENTRY_COUNT = 500;

/** Maximum local storage size in bytes (1 GB). */
const MAX_STORAGE_BYTES = 1_073_741_824;

/** Maximum number of items allowed in the sync queue. */
const MAX_SYNC_QUEUE_SIZE = 1000;

/** Maximum number of notes allowed in local storage. */
export const MAX_NOTE_COUNT = 10000;

/**
 * Dexie database instance for the Kira Productivity Suite.
 */
export class JournalAppDB extends Dexie {
  // ── Existing v1 stores ────────────────────────────────────────────────────
  entries!: Table<LocalJournalEntry, string>;
  pendingMedia!: Table<PendingMedia, string>;
  syncQueue!: Table<SyncQueueItem, number>;

  // ── New v2 stores ─────────────────────────────────────────────────────────
  tasks!: Table<Task, string>;
  taskLists!: Table<TaskList, string>;
  tags!: Table<Tag, string>;
  habits!: Table<Habit, string>;
  habitCompletions!: Table<HabitCompletion, [string, string]>;
  calendarEvents!: Table<CalendarEvent, string>;
  pomodoroSessions!: Table<PomodoroSession, string>;
  countdowns!: Table<CountdownTimer, string>;
  calendarConnections!: Table<ExternalCalendarConnection, string>;
  appSettings!: Table<AppSetting, string>;

  // ── New v3 stores ─────────────────────────────────────────────────────────
  notes!: Table<Note, string>;

  constructor() {
    super('JournalAppDB');

    this.version(1).stores({
      entries: 'entryDate, syncStatus, updatedAt',
      pendingMedia: 'localId, status',
      syncQueue: '++id, entryDate, timestamp',
    });

    this.version(2).stores({
      // Existing stores (must be repeated to preserve them)
      entries: 'entryDate, syncStatus, updatedAt',
      pendingMedia: 'localId, status',
      syncQueue: '++id, module, entityId, timestamp',
      // New productivity module stores
      tasks: 'taskId, userId, listId, priority, dueDate, isCompleted, updatedAt, syncStatus, deleted',
      taskLists: 'listId, userId, sortOrder, syncStatus',
      tags: 'tagId, userId',
      habits: 'habitId, userId, isArchived, syncStatus',
      habitCompletions: '[habitId+date], userId, habitId, date, syncStatus',
      calendarEvents: 'eventId, userId, startDate, endDate, source, connectionId, syncStatus',
      pomodoroSessions: 'sessionId, userId, taskId, startedAt, status, syncStatus',
      countdowns: 'countdownId, userId, targetDate, isExpired, syncStatus',
      calendarConnections: 'connectionId, userId, provider, status',
      appSettings: 'key',
    });

    this.version(3).stores({
      // Existing v1/v2 stores — repeated to preserve them
      entries: 'entryDate, syncStatus, updatedAt',
      pendingMedia: 'localId, status',
      syncQueue: '++id, module, entityId, timestamp',
      tasks: 'taskId, userId, listId, priority, dueDate, isCompleted, updatedAt, syncStatus, deleted',
      taskLists: 'listId, userId, sortOrder, syncStatus',
      tags: 'tagId, userId',
      habits: 'habitId, userId, isArchived, syncStatus',
      habitCompletions: '[habitId+date], userId, habitId, date, syncStatus',
      calendarEvents: 'eventId, userId, startDate, endDate, source, connectionId, syncStatus',
      pomodoroSessions: 'sessionId, userId, taskId, startedAt, status, syncStatus',
      countdowns: 'countdownId, userId, targetDate, isExpired, syncStatus',
      calendarConnections: 'connectionId, userId, provider, status',
      appSettings: 'key',
      // New v3 store
      notes: 'noteId, userId, pinned, updatedAt, syncStatus, deleted',
    });
  }
}

/** Singleton database instance. */
export const db = new JournalAppDB();

// ---------------------------------------------------------------------------
// Entry CRUD operations
// ---------------------------------------------------------------------------

/**
 * Get a single journal entry by date.
 * @param entryDate - ISO date string (YYYY-MM-DD)
 */
export async function getEntry(entryDate: string): Promise<LocalJournalEntry | undefined> {
  return db.entries.get(entryDate);
}

/**
 * Get all local journal entries, sorted by entryDate descending.
 */
export async function getAllEntries(): Promise<LocalJournalEntry[]> {
  return db.entries.orderBy('entryDate').reverse().toArray();
}

/**
 * Upsert a journal entry. If the entry doesn't exist, checks storage limits
 * before inserting.
 * @param entry - The journal entry to create or update.
 * @throws Error if storage limits (500 entries or 1GB) would be exceeded on insert.
 */
export async function putEntry(entry: LocalJournalEntry): Promise<void> {
  const existing = await db.entries.get(entry.entryDate);

  if (!existing) {
    // New entry — enforce limits
    const count = await getEntryCount();
    if (count >= MAX_ENTRY_COUNT) {
      throw new Error(
        `Offline storage limit reached: maximum ${MAX_ENTRY_COUNT} entries allowed.`
      );
    }

    const storageBytes = await getStorageEstimate();
    if (storageBytes >= MAX_STORAGE_BYTES) {
      throw new Error(
        'Offline storage limit reached: maximum 1GB of local storage allowed.'
      );
    }
  }

  await db.entries.put(entry);
}

/**
 * Delete a journal entry by date.
 * @param entryDate - ISO date string (YYYY-MM-DD)
 */
export async function deleteEntry(entryDate: string): Promise<void> {
  await db.entries.delete(entryDate);
}

/**
 * Get the total number of journal entries in local storage.
 */
export async function getEntryCount(): Promise<number> {
  return db.entries.count();
}

/**
 * Get estimated storage usage in bytes.
 * Uses the StorageManager API when available, otherwise falls back
 * to a rough estimation based on entry content sizes.
 */
export async function getStorageEstimate(): Promise<number> {
  // Try the Storage API (available in modern browsers and Electron)
  if (navigator?.storage?.estimate) {
    const estimate = await navigator.storage.estimate();
    return estimate.usage ?? 0;
  }

  // Fallback: estimate by summing content lengths of all entries
  const entries = await db.entries.toArray();
  let totalBytes = 0;
  for (const entry of entries) {
    totalBytes += new Blob([entry.content]).size;
    // Account for metadata overhead (rough estimate)
    totalBytes += 512;
  }

  // Add pending media sizes
  const media = await db.pendingMedia.toArray();
  for (const item of media) {
    totalBytes += item.blob.size;
  }

  return totalBytes;
}

// ---------------------------------------------------------------------------
// Sync queue operations
// ---------------------------------------------------------------------------

/**
 * Add an item to the sync queue. Enforces max 1000 items.
 * @param item - The sync queue item (id will be auto-generated).
 * @throws Error if queue already has 1000 items.
 */
export async function enqueueSyncItem(
  item: Omit<SyncQueueItem, 'id'>
): Promise<void> {
  const size = await getSyncQueueSize();
  if (size >= MAX_SYNC_QUEUE_SIZE) {
    throw new Error(
      `Sync queue is full: maximum ${MAX_SYNC_QUEUE_SIZE} queued changes allowed.`
    );
  }
  await db.syncQueue.add(item as SyncQueueItem);
}

/**
 * Dequeue (get and remove) the oldest item from the sync queue (FIFO).
 * @returns The oldest sync queue item, or undefined if empty.
 */
export async function dequeueSyncItem(): Promise<SyncQueueItem | undefined> {
  return db.transaction('rw', db.syncQueue, async () => {
    const oldest = await db.syncQueue.orderBy('id').first();
    if (oldest && oldest.id != null) {
      await db.syncQueue.delete(oldest.id as unknown as number);
    }
    return oldest;
  });
}

/**
 * Get the current number of items in the sync queue.
 */
export async function getSyncQueueSize(): Promise<number> {
  return db.syncQueue.count();
}

// ---------------------------------------------------------------------------
// Pending media operations
// ---------------------------------------------------------------------------

/**
 * Store a pending media blob for later upload.
 * @param media - The pending media item to store.
 */
export async function addPendingMedia(media: PendingMedia): Promise<void> {
  await db.pendingMedia.put(media);
}

/**
 * Get all pending media items awaiting upload.
 */
export async function getPendingMedia(): Promise<PendingMedia[]> {
  return db.pendingMedia.toArray();
}

/**
 * Remove a pending media item after successful upload.
 * @param localId - The local ID of the media item to remove.
 */
export async function removePendingMedia(localId: string): Promise<void> {
  await db.pendingMedia.delete(localId);
}
